import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Building2,
  Activity,
  LifeBuoy,
  ScrollText,
  UsersRound,
  LogOut,
  Menu,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  FileCheck2,
  FlaskConical,
  Tags,
  ServerCog,
  SlidersHorizontal,
  Megaphone,
} from 'lucide-react';
import { usePlatformStore } from '../stores/platform.store';
import { Avatar } from './ui';
import { cn } from '../lib/cn';

/**
 * PlatformLayout — Custel operatör (SaaS personeli) konsolu.
 *
 * Müşteri (tenant) `DashboardLayout`'unun yapısını/kalitesini yansıtır
 * (ink sidebar + outlet, responsive, collapse, erişilebilir NavLink'ler)
 * FAKAT vurgu rengi lime DEĞİL **magenta/violet** — operatör her zaman
 * müşteri tenant'ında değil platform konsolunda olduğunu bilsin.
 *
 * Vurgu rengi layout kökünde scope edilmiş CSS custom property'leri ile
 * tanımlanır (`--plat-accent`). Tailwind arbitrary value'ları bu var'ı okur.
 */

type PlatformNavItem = {
  to: string;
  /** i18n key (relative to "nav" namespace), resolved with t() at render time. */
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
};

const PLATFORM_NAV: PlatformNavItem[] = [
  { to: '/platform', label: 'platform.overview', icon: LayoutDashboard, end: true },
  { to: '/platform/musteriler', label: 'platform.customers', icon: Building2 },
  { to: '/platform/metrikler', label: 'platform.systemMetrics', icon: Activity },
  { to: '/platform/talepler', label: 'platform.supportTickets', icon: LifeBuoy },
  { to: '/platform/erisim-kayitlari', label: 'platform.accessLogs', icon: ScrollText },
  { to: '/platform/ekip', label: 'platform.team', icon: UsersRound },
  { to: '/platform/ayarlar', label: 'platform.runtimeSettings', icon: SlidersHorizontal },
  { to: '/platform/kyb', label: 'platform.kybQueue', icon: FileCheck2 },
  { to: '/platform/pricing-experiments', label: 'platform.pricingAbTest', icon: FlaskConical },
  { to: '/platform/enterprise-pricing', label: 'platform.enterprisePricing', icon: Tags },
  { to: '/platform/altyapi', label: 'platform.infraMetrics', icon: ServerCog },
  { to: '/platform/kampanyalar', label: 'platform.campaigns', icon: Megaphone },
];

const COLLAPSE_KEY = 'custel-platform-sidebar-collapsed';

// Platform vurgu paleti — magenta/violet. Müşteri (lime) ile bilinçli kontrast.
const platformAccentStyle = {
  '--plat-accent': '#a21caf', // magenta-700
  '--plat-accent-soft': 'rgba(162, 28, 175, 0.16)',
  '--plat-accent-strong': '#7e22ce', // violet-700
} as CSSProperties;

const navItemBase =
  'group relative flex items-center gap-2.5 rounded-r-custel-md px-3 py-2 text-sm font-normal text-custel-g300 transition-all duration-custel-fast hover:bg-white/[0.04] hover:text-custel-krem focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--plat-accent)]';

export function PlatformLayout() {
  const { t } = useTranslation('nav');
  const staff = usePlatformStore((s) => s.staff);
  const logout = usePlatformStore((s) => s.logout);
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* private mode — ignore */
    }
  }, [collapsed]);

  // Route değişince mobile drawer'ı kapat.
  useMemo(() => setMobileOpen(false), [location.pathname]);

  function handleLogout() {
    void logout();
  }

  const sidebar = (
    <>
      <div className="flex items-center justify-between border-b border-white/[0.08] px-4 py-4">
        <Link
          to="/platform"
          className="flex items-center gap-2.5 rounded-custel-sm focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--plat-accent)]"
          aria-label={t('platform.homeAria')}
          title={collapsed ? t('platform.console') : undefined}
        >
          {/* Logo badge: lime yerine magenta vurgu kare + shield ikon. */}
          <span
            aria-hidden="true"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-custel-md text-white"
            style={{ background: 'var(--plat-accent)' }}
          >
            <ShieldCheck size={16} />
          </span>
          {!collapsed && (
            <span className="min-w-0">
              <span className="block text-[15px] font-semibold leading-tight tracking-tight text-custel-krem">
                Custel
              </span>
              <span
                className="block truncate text-[11px] font-medium uppercase leading-tight tracking-[0.08em]"
                style={{ color: 'var(--plat-accent)' }}
              >
                {t('platform.console')}
              </span>
            </span>
          )}
        </Link>
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? t('sidebar.expandMenu') : t('sidebar.collapseMenu')}
          className="hidden h-6 w-6 shrink-0 items-center justify-center rounded text-custel-g500 transition-colors hover:bg-white/[0.06] hover:text-custel-krem focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[color:var(--plat-accent)] md:flex"
        >
          {collapsed ? <ChevronRight size={14} aria-hidden="true" /> : <ChevronLeft size={14} aria-hidden="true" />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto p-2">
        <div className="space-y-0.5">
          {PLATFORM_NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              title={collapsed ? t(label) : undefined}
              className={({ isActive }) =>
                cn(navItemBase, isActive && 'font-medium text-custel-krem', collapsed && 'justify-center px-2')
              }
              style={({ isActive }) => (isActive ? { backgroundColor: 'var(--plat-accent-soft)' } : undefined)}
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span
                      aria-hidden="true"
                      className="absolute inset-y-1.5 left-0 w-[3px] rounded-r"
                      style={{ background: 'var(--plat-accent)' }}
                    />
                  )}
                  <Icon size={16} aria-hidden="true" />
                  {!collapsed && <span className="flex-1 truncate">{t(label)}</span>}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {!collapsed && (
        <div className="border-t border-white/[0.08] px-4 py-3">
          <p className="text-[11px] leading-snug text-custel-g500">{t('platform.staffNotice')}</p>
        </div>
      )}
    </>
  );

  return (
    <div className="flex min-h-screen bg-custel-krem" style={platformAccentStyle}>
      {/* Desktop sidebar — ink bg (müşteri ile aynı), magenta aktif vurgu. */}
      <aside
        className={cn(
          'sticky top-0 hidden h-screen shrink-0 flex-col bg-custel-ink text-custel-krem transition-[width] duration-custel-base md:flex',
          collapsed ? 'w-16' : 'w-60',
        )}
        aria-label={t('platform.nav')}
      >
        {sidebar}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <button
            type="button"
            aria-label={t('sidebar.closeMenu')}
            onClick={() => setMobileOpen(false)}
            className="fixed inset-0 z-40 md:hidden"
            style={{ backgroundColor: 'var(--custel-overlay)' }}
          />
          <aside className="fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-custel-ink text-custel-krem shadow-custel-drawer md:hidden">
            {sidebar}
          </aside>
        </>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header
          className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 border-b bg-custel-paper px-4 md:px-6"
          style={{ borderBottomColor: 'var(--plat-accent-soft)' }}
        >
          <div className="flex flex-1 items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label={t('sidebar.openMenu')}
              className="rounded-custel-md p-1 text-custel-g500 hover:bg-custel-g100 hover:text-custel-ink focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custel-ink md:hidden"
            >
              <Menu size={20} aria-hidden="true" />
            </button>
            <span
              className="inline-flex items-center gap-1.5 rounded-custel-pill px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-white"
              style={{ background: 'var(--plat-accent)' }}
            >
              <ShieldCheck size={12} aria-hidden="true" />
              {t('platform.console')}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <span className="block text-sm font-medium leading-tight text-custel-ink">{staff?.name ?? '—'}</span>
              <span className="block text-[11px] leading-tight text-custel-text-tertiary">{staff?.email ?? '—'}</span>
            </div>
            <Avatar name={staff?.name ?? ''} size="md" current />
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex h-9 items-center gap-1.5 rounded-custel-md px-3 text-sm text-custel-g700 transition-colors hover:bg-custel-g100 hover:text-custel-ink focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custel-ink"
              aria-label={t('platform.logoutAria')}
            >
              <LogOut size={16} aria-hidden="true" />
              <span className="hidden sm:inline">{t('menu.logout')}</span>
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-auto bg-custel-krem p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
